import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { nanoid } from 'nanoid';
import { IconButton } from '@material-ui/core';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';

import { MapCard, GridCard, CardDb, isMapCard, isGridCard } from '../redux/cardDbSlice';
import { MarkerShapeType, ArrowDb } from '../redux/arrowDbSlice';
import useOneCardDb from '../hooks/useOneCardDb';
import useImageData from '../hooks/useImageData';
import { getArrowStrokeWidth, getArrowDasharray } from '../general';
import { getCardSize } from '../card_components';
import { ArrowMenuAction } from './BookEditor';

type HighlightSvgProps = {
  drawHeight: number;
  selected?: boolean;
}
const HighlightSvg = styled.svg<HighlightSvgProps>`
  position: absolute;
  transform-origin: ${props => 'left ' + (props.drawHeight / 2) + 'px'};
  z-index: ${props => props.selected ? 2 : -1};
`
type ClickAreaSvgProps = {
  drawHeight: number;
}
const ClickAreaSvg = styled.svg<ClickAreaSvgProps>`
  position: absolute;
  transform-origin: ${props => 'left ' + (props.drawHeight / 2) + 'px'};
  z-index: 0;
`
type ArrowSvgProps = {
  drawHeight: number;
  preview: boolean;
  selected?: boolean;
}
const ArrowSvg = styled.svg<ArrowSvgProps>`
  position: ${props => props.preview ? 'relative' : 'absolute'};
  transform-origin: ${props => 'left ' + (props.drawHeight / 2) + 'px'};
  z-index: ${props => props.selected ? 3 : 1};
`
const LinkIconButton = styled(IconButton)`
  opacity: 0.8;
`

const isCardHavingCoordinate = (obj: any): obj is MapCard | GridCard => {
  return isMapCard(obj) || isGridCard(obj);
}

type ArrowTailProps = {
  id: string;
  data: ArrowDb;
  markerSize: number;
}
const ArrowTail: React.FC<ArrowTailProps> = ({id, data, markerSize}) => {
  const shape = data.shapes.tail.shape;
  const strokeWidth = getArrowStrokeWidth(data);
  const lineStyleEffect = data.lineStyle === 'double' ? 1.5 + strokeWidth / 2 : 0;
  const verticalCenter = markerSize / 2 - lineStyleEffect;
  const viewBox = '0 0 ' + markerSize + ' ' + markerSize;

  let d = '';
  if (shape === 'arrow') d = 'M ' + markerSize + ',0 L ' + markerSize + ',' + markerSize + ' L 0,' + (markerSize / 2) + ' Z';
  else if (shape === 'circle') {
    d = 'M 0,' + (markerSize / 2) + ' A ' + (markerSize / 2) + ' ' + (markerSize / 2) + ' 0 1 1 ' + markerSize + ',' + (markerSize / 2) +
      ' A' + (markerSize / 2) + ' ' + (markerSize / 2) + ' 0 1 1 0,' + (markerSize / 2);
  }
  else if (shape === 'rectangle') d = 'M 0,0 H ' + markerSize + ' V ' + markerSize + ' H 0 Z';
  else if (shape === 'rhombus') {
    d = 'M 0,' + (markerSize / 2) + ' L ' + (markerSize / 2) + ', 0 L ' + markerSize + ',' + (markerSize / 2) +
      ' L ' + (markerSize / 2) + ',' + markerSize + ' Z';
  }
  else if (shape === 'crow') {
    d = 'M ' + markerSize + ',' + (markerSize / 2) + ' L 0,0' +
      ' M ' + markerSize + ',' + (markerSize / 2) + ' H 0' +
      ' M ' + markerSize + ',' + (markerSize / 2) + ' L 0,' + markerSize;
  }

  if (shape === 'flat') {
    return <></>;
  } else if (shape === 'crow') {
    return (
      <marker id={id} markerUnits="userSpaceOnUse" viewBox={viewBox} refX={markerSize - 2} refY={verticalCenter} markerWidth={markerSize} markerHeight={markerSize} orient="auto">
        <path d={d} stroke={data.color} strokeWidth="2" fill="none" />
      </marker>
    );
  } else {
    return (
      <marker id={id} markerUnits="userSpaceOnUse" viewBox={viewBox} refX={markerSize} refY={verticalCenter} markerWidth={markerSize} markerHeight={markerSize} orient="auto">
        <path d={d} stroke="none" fill={data.color} />
      </marker>
    );
  }
}

type ArrowHeadProps = {
  id: string;
  data: ArrowDb;
  markerSize: number;
}
const ArrowHead: React.FC<ArrowHeadProps> = ({id, data, markerSize}) => {
  const shape = data.shapes.head.shape;
  const strokeWidth = getArrowStrokeWidth(data);
  const lineStyleEffect = data.lineStyle === 'double' ? 1.5 + strokeWidth / 2 : 0;
  const verticalCenter = markerSize / 2 + lineStyleEffect;
  const viewBox = '0 0 ' + markerSize + ' ' + markerSize;

  let d = '';
  if (shape === 'arrow') d = 'M 0,0 L 0,' + markerSize + ' L ' + markerSize + ',' + (markerSize / 2) + ' Z';
  else if (shape === 'circle') {
    d = 'M 0,' + (markerSize / 2) + ' A ' + (markerSize / 2) + ' ' + (markerSize / 2) + ' 0 1 1 ' + markerSize + ',' + (markerSize / 2) +
      ' A' + (markerSize / 2) + ' ' + (markerSize / 2) + ' 0 1 1 0,' + (markerSize / 2);
  }
  else if (shape === 'rectangle') d = 'M 0,0 H ' + markerSize + ' V ' + markerSize + ' H 0 Z';
  else if (shape === 'rhombus') {
    d = 'M 0,' + (markerSize / 2) + ' L ' + (markerSize / 2) + ', 0 L ' + markerSize + ',' + (markerSize / 2) +
      ' L ' + (markerSize / 2) + ',' + markerSize + ' Z';
  }
  else if (shape === 'crow') {
    d = 'M 0,' + (markerSize / 2) + ' L ' + markerSize + ',0' +
      ' M 0,' + (markerSize / 2) + ' H ' + markerSize +
      ' M 0,' + (markerSize / 2) + ' L ' + markerSize + ',' + markerSize;
  }

  if (shape === 'flat') {
    return <></>;
  } else if (shape === 'crow') {
    return (
      <marker id={id} markerUnits="userSpaceOnUse" viewBox={viewBox} refX={2} refY={verticalCenter} markerWidth={markerSize} markerHeight={markerSize} orient="auto">
        <path d={d} stroke={data.color} strokeWidth="2" fill="none" />
      </marker>
    );
  } else {
    return (
      <marker id={id} markerUnits="userSpaceOnUse" viewBox={viewBox} refX="0" refY={verticalCenter} markerWidth={markerSize} markerHeight={markerSize} orient="auto">
        <path d={d} stroke="none" fill={data.color} />
      </marker>
    );
  }
}

type ArrowLabelProps = {
  data: ArrowDb;
  position: string;
  length: number;
  dStart: number;
  dEnd: number;
  reverse: boolean;
}
const ArrowLabel: React.FC<ArrowLabelProps> = ({data, position, length, dStart, dEnd, reverse}) => {
  const getAnchor = () => {
    if (position === 'head1') return reverse ? 'start' : 'end';
    if (position === 'head2') return reverse ? 'start' : 'end';
    if (position === 'middle1') return 'middle';
    if (position === 'middle2') return 'middle';
    if (position === 'tail1') return reverse ? 'end' : 'start';
    if (position === 'tail2') return reverse ? 'end' : 'start';
  }
  const getX = () => {
    if (!reverse && !data.return) {
      switch (position) {
        case 'head1': return length - dEnd;
        case 'head2': return length - dEnd;
        case 'middle1': return length / 2;
        case 'middle2': return length / 2;
        case 'tail1': return dStart;
        case 'tail2': return dStart;
      }
    } else if (reverse && !data.return) {
      switch (position) {
        case 'head1': return dEnd;
        case 'head2': return dEnd;
        case 'middle1': return length / 2;
        case 'middle2': return length / 2;
        case 'tail1': return length - dStart;
        case 'tail2': return length - dStart;
      }
    } else if (!reverse && data.return) {
      switch (position) {
        case 'head1': return length;
        case 'head2': return length;
        case 'middle1': return length / 2;
        case 'middle2': return length / 2;
        case 'tail1': return dStart;
        case 'tail2': return dEnd;
      }
    } else if (reverse && data.return) {
      switch (position) {
        case 'head1': return 0;
        case 'head2': return 0;
        case 'middle1': return length / 2;
        case 'middle2': return length / 2;
        case 'tail1': return length - dStart;
        case 'tail2': return length - dEnd;
      }
    }
  }
  const getY = () => {
    const up = data.lineStyle !== 'double' && data.return ? 14 : 24;
    const down = data.lineStyle !== 'double' && data.return ? 60 : 52;
    if (position === 'head1') return reverse ? down : up;
    if (position === 'head2') return reverse ? up : down;
    if (position === 'middle1') return reverse ? down : up;
    if (position === 'middle2') return reverse ? up : down;
    if (position === 'tail1') return reverse ? down : up;
    if (position === 'tail2') return reverse ? up : down;
  }
  const getLabel = () => {
    if (position === 'head1') return data.labels.head1.label;
    if (position === 'head2') return data.labels.head2.label;
    if (position === 'middle1') return data.labels.middle1.label;
    if (position === 'middle2') return data.labels.middle2.label;
    if (position === 'tail1') return data.labels.tail1.label;
    if (position === 'tail2') return data.labels.tail2.label;
  }

  return (
    <text textAnchor={getAnchor()} x={getX()} y={getY()}
      style={{transformOrigin: 'center', transform: reverse ? 'rotate(180deg)' : undefined, userSelect: 'none'}}
      // textLength={lineLength / 3} lengthAdjust="spacingAndGlyphs"
      fontSize="16"
      pointerEvents="all"
    >
      { getLabel() }
    </text>
  );
}

type ArrowProps = {
  id: string;
  data: ArrowDb;
  tailCard?: CardDb;
  headCard?: CardDb;
  sibLength: number;
  editPreview?: boolean;
  editPreviewLength?: number;
  linkPreview?: boolean;
  selected?: boolean;
  arrowMenuClicked?: ArrowMenuAction;
  onContextMenu?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
}
const Arrow: React.FC<ArrowProps> = (props) => {
  const { id, data, sibLength, editPreview, editPreviewLength, linkPreview, selected, arrowMenuClicked, onContextMenu } = props;
  const [elementId, setElementId] = useState('');
  const tailCardValue = useOneCardDb(data.fromId);
  const tailCard = props.tailCard ? props.tailCard : tailCardValue;
  const headCardValue = useOneCardDb(data.toId);
  const headCard = props.headCard ? props.headCard : headCardValue;
  const imageUrl = useImageData(data.imageId);

  useEffect(() => setElementId(id + nanoid()), [data])

  if (!isCardHavingCoordinate(tailCard) || !isCardHavingCoordinate(headCard)) return <></>;

  const fixed90InRadian = Number((Math.PI / 2).toFixed(10));
  const fixed180InRadian = Number((Math.PI).toFixed(10));
  const fixed270InRadian = Number((Math.PI * 3 / 2).toFixed(10));

  const calcHypotenuse = (a: number, b: number) => {
    return (Math.sqrt((a * a) + (b * b)));
  }

  const tailCardWidth = getCardSize(tailCard).width;// width of tail side card
  const tailCardHeight = getCardSize(tailCard).height;// height of tail side card
  const headCardWidth = getCardSize(headCard).width;// width of head side card
  const headCardHeight = getCardSize(headCard).height;// height of head side card
  const tailCardCenterX = tailCard.x + tailCardWidth / 2;// x coordinate of tail side card's center
  const tailCardCenterY = tailCard.y + tailCardHeight / 2;// y coordinate of tail side card's center
  const headCardCenterX = headCard.x + headCardWidth / 2;// x coordinate of head side card's center
  const headCardCenterY = headCard.y + headCardHeight / 2;// y coordinate of head side card's center
  const diffCenterX = headCardCenterX - tailCardCenterX;// distance in x direction between the cards' center
  const diffCenterY = headCardCenterY - tailCardCenterY;// distance in y direction between the cards' center
  const tanTailCardDiagonal = tailCardHeight / tailCardWidth;// tangent of tail side card's height and width
  const tanHeadCardDiagonal = headCardHeight / headCardWidth;// tangent of head side card's height and width
  const tanCenters = diffCenterX === 0 ? undefined : Math.abs(diffCenterY / diffCenterX);// tangent of the cards' center

  const makePosiRadian = (radian: number) => {
    const posiRadian = radian >= 0 ? radian : radian + Math.PI * 2;
    return Number((posiRadian).toFixed(10));
  }
  const relativeCos = (radian: number) => {
    if (0 <= radian && radian < fixed90InRadian) return Math.cos(radian);
    else if (fixed90InRadian <= radian && radian < fixed180InRadian) return -Math.cos(radian);
    else if (fixed180InRadian <= radian && radian < fixed270InRadian) return Math.cos(radian);
    else return -Math.cos(radian);
  }
  const relativeTan = (radian: number) => {
    if (0 <= radian && radian < fixed90InRadian) return Math.tan(radian);
    else if (radian === fixed90InRadian) return undefined;
    else if (fixed90InRadian < radian && radian < fixed180InRadian) return -Math.tan(radian);
    else if (fixed180InRadian <= radian && radian < fixed270InRadian) return Math.tan(radian);
    else if (radian === fixed270InRadian) return undefined;
    else return -Math.tan(radian);
  }
  const getMove = (radian: number) => {
    const tanRadian = radian === fixed90InRadian ? undefined : Math.tan(radian);
    const tanRadianMinus90 = tanRadian === undefined ? 0 : tanRadian === 0 ? undefined : -1 / tanRadian;
    let moveMaxTail = 0;
    let moveMaxHead = 0;
    if (tanRadianMinus90 === undefined) moveMaxTail = tailCardHeight / 2;
    else if (-tanTailCardDiagonal <= tanRadianMinus90 && tanRadianMinus90 <= tanTailCardDiagonal) moveMaxTail = Math.abs(tailCardWidth / 2 / Math.sin(radian));
    else if (tanTailCardDiagonal < tanRadianMinus90 || tanRadianMinus90 < tanTailCardDiagonal) moveMaxTail = Math.abs(tailCardHeight / 2 / Math.cos(radian));
    if (tanRadianMinus90 === undefined) moveMaxHead = headCardHeight / 2;
    else if (-tanHeadCardDiagonal <= tanRadianMinus90 && tanRadianMinus90 <= tanHeadCardDiagonal) moveMaxHead = Math.abs(headCardWidth / 2 / Math.sin(radian));
    else if (tanHeadCardDiagonal < tanRadianMinus90 || tanRadianMinus90 < tanHeadCardDiagonal) moveMaxHead = Math.abs(headCardHeight / 2 / Math.cos(radian));
    const moveMax = Math.min(moveMaxTail, moveMaxHead);
    const moveUnit = 20 * (sibLength - 1) < moveMax ? 20 : moveMax * 2 / (sibLength - 1) / 2;
    const moveCoefficient = sibLength - 2 * data.order - 1;
    const move = moveCoefficient * moveUnit;
    return move;
  }

  const calcExe = () => {
    let angleRadian = 0;// arrow's angle in radian
    let relativeFromX = 0;// distance in x direction between arrow's tail and tail side card's center
    let relativeFromY = 0;// distance in y direction between arrow's tail and tail side card's center
    let fromX = 0;// x coordinate of arrow's tail
    let fromY = 0;// y coordinate of arrow's tail
    let relativeToX = 0;// distance in x direction between arrow's head and head side card's center
    let relativeToY = 0;// distance in y direction between arrow's head and head side card's center
    let toX = 0;// x coordinate of arrow's head
    let toY = 0;// y coordinate of arrow's head

    // when head side card at lower right of tail side card
    if (tanCenters !== undefined && diffCenterX > 0 && diffCenterY >= 0) angleRadian = makePosiRadian(Math.atan(tanCenters));
    // when head side card at just lower of tail side card
    else if (tanCenters === undefined && diffCenterY >= 0) angleRadian = fixed90InRadian;
    // when head side card at lower left of tail side card
    else if (tanCenters !== undefined && diffCenterX < 0 && diffCenterY > 0) angleRadian = makePosiRadian(Math.atan(-tanCenters) + Math.PI);
    // when head side card at upper left of tail side card
    else if (tanCenters !== undefined && diffCenterX < 0 && diffCenterY <= 0) angleRadian = makePosiRadian(Math.atan(tanCenters) + Math.PI);
    // when head side card at just upper of tail side card
    else if (tanCenters === undefined) angleRadian = fixed270InRadian;
    // when head side card at upper right of tail side card
    else angleRadian = makePosiRadian(Math.atan(-tanCenters));

    const move = getMove(angleRadian);

    // when (90° < angle < 180° || 270° < angle < 360°) use (180° - angle) instead of angle in calculation
    const sinAngle = Math.sin(angleRadian);
    const relativeCosAngle = relativeCos(angleRadian);
    const relativeTanAngle = relativeTan(angleRadian);

    if (relativeTanAngle === undefined || angleRadian === fixed90InRadian || angleRadian === fixed270InRadian) {// when the cards line up perpendicular
      relativeFromX = move;
      relativeFromY = tailCardHeight / 2;
    } else if (Math.abs(move / relativeCosAngle + tailCardWidth / 2 * relativeTanAngle) <= tailCardHeight / 2) {// when arrow tail at card's upper and lower side
      relativeFromX = tailCardWidth / 2;
      relativeFromY = move / relativeCosAngle + tailCardWidth / 2 * relativeTanAngle;
    } else {// when arrow tail at card's right and left side
      relativeFromX = (tailCardHeight / 2 - move / relativeCosAngle) / relativeTanAngle;
      relativeFromY = tailCardHeight / 2;
    }
    if (relativeTanAngle === undefined || angleRadian === fixed90InRadian || angleRadian === fixed270InRadian) {// when the cards line up perpendicular
      relativeToX = move;
      relativeToY = -headCardHeight / 2;
    } else if (Math.abs(move / relativeCosAngle - headCardWidth / 2 * relativeTanAngle) <= headCardHeight / 2) {// when arrow head at card's upper and lower side
      relativeToX = -headCardWidth / 2;
      relativeToY = move / relativeCosAngle - headCardWidth / 2 * relativeTanAngle;
    } else {// when arrow head at card's right and left side
      relativeToX = -(move / sinAngle + headCardHeight / 2 / relativeTanAngle);
      relativeToY = -headCardHeight / 2;
    }

    if (0 <= angleRadian && angleRadian < fixed90InRadian) {// when head side card at lower right of tail side card
      fromX = tailCardCenterX + relativeFromX;
      fromY = tailCardCenterY + relativeFromY;
      toX = headCardCenterX + relativeToX;
      toY = headCardCenterY + relativeToY;
    } else if (angleRadian === fixed90InRadian) {// when head side card at just lower of tail side card
      fromX = tailCardCenterX - relativeFromX;
      fromY = tailCardCenterY + relativeFromY;
      toX = headCardCenterX - relativeToX;
      toY = headCardCenterY + relativeToY;
    } else if (fixed90InRadian < angleRadian && angleRadian < fixed180InRadian) {// when head side card at lower left of tail side card
      fromX = tailCardCenterX - relativeFromX;
      fromY = tailCardCenterY + relativeFromY;
      toX = headCardCenterX - relativeToX;
      toY = headCardCenterY + relativeToY;
    } else if (fixed180InRadian <= angleRadian && angleRadian < fixed270InRadian) {// when head side card at upper left of tail side card
      fromX = tailCardCenterX - relativeFromX;
      fromY = tailCardCenterY - relativeFromY;
      toX = headCardCenterX - relativeToX;
      toY = headCardCenterY - relativeToY;
    } else if (angleRadian === fixed270InRadian) { // when head side card at just upper of tail side card
      fromX = tailCardCenterX - relativeFromX;
      fromY = tailCardCenterY - relativeFromY;
      toX = headCardCenterX - relativeToX;
      toY = headCardCenterY - relativeToY;
    } else {// when head side card at upper right of tail side card
      fromX = tailCardCenterX + relativeFromX;
      fromY = tailCardCenterY - relativeFromY;
      toX = headCardCenterX + relativeToX;
      toY = headCardCenterY - relativeToY;
    }

    return { angleRadian, fromX, fromY, toX, toY };
  }

  const { angleRadian, fromX, fromY, toX, toY } = calcExe();

  const diffX = toX - fromX;
  const diffY = toY - fromY;
  let length = calcHypotenuse(diffX, diffY);// arrow's length
  if ((Math.abs(diffCenterX) <= tailCardWidth / 2 + headCardWidth / 2) && (Math.abs(diffCenterY) <= tailCardHeight / 2 + headCardHeight / 2)) length = 0;
  if (editPreview) length = editPreviewLength || 1000;

  const drawHeight = 64;
  const viewBoxValue = '0 0 ' + length + ' ' + drawHeight;
  const angle = angleRadian / fixed180InRadian * 180;// arrow's angle

  const transformValue = editPreview ? undefined : linkPreview ? 'rotate(' + angle + ')' :
    'translate(0, ' + (-drawHeight / 2) + ') translate(' + fromX +', ' + fromY +') rotate(' + angle + ')';

  const getMarkerSize = (basicSize: number, marker: MarkerShapeType) => {
    let size = marker.shape === 'crow' ? basicSize * 1.5 : basicSize;
    if (marker.shape !== 'flat' && marker.size === 'large') size = size * 1.5;
    else if (marker.shape !== 'flat' && marker.size === 'small') size = size / 1.5;
    return size;
  }
  const basicMarkerSize = 12;
  const tailSize = getMarkerSize(basicMarkerSize, data.shapes.tail);
  const headSize = getMarkerSize(basicMarkerSize, data.shapes.head);

  const strokeWidth = getArrowStrokeWidth(data);
  const isTailFlat = data.shapes.tail.shape === 'flat';
  const isHeadFlat = data.shapes.head.shape === 'flat';
  const dStart = isTailFlat ? 0 : tailSize;
  const dEnd = isHeadFlat ? 0 : headSize;
  let d = '';
  if (data.lineStyle === 'double') {
    const top = drawHeight / 2 - 1.5 - strokeWidth / 2;
    const bottom = drawHeight - top;
    d = 'M ' + dStart + ',' + top + ' H ' + (length - dEnd) +
      ' M ' + dStart + ',' + bottom + ' H ' + (length - dEnd);
  } else if (data.return) {
    const top = strokeWidth + tailSize / 2 + 14;
    const bottom = drawHeight - (strokeWidth + headSize / 2 + 14);
    const markerAverageHarf = (headSize + tailSize) / 2 / 2;
    const radius = drawHeight / 2 - strokeWidth - markerAverageHarf - 14;
    d = 'M ' + dStart + ',' + top + ' H ' + (length - radius - strokeWidth / 2) +
      ' A ' + radius + ' ' + radius + ' 0 1 1 ' + (length - radius - strokeWidth / 2) + ',' + bottom +
      ' H ' + dEnd;
  } else {
    d = 'M ' + dStart + ',' + (drawHeight / 2) + ' H ' + (length - dEnd);
  }

  const labelKeys = Object.keys(data.labels);
  let labelReverse = false;
  if (!editPreview && fixed90InRadian < angleRadian && angleRadian <= fixed270InRadian) labelReverse = true;

  const linkButtonSize = 30;
  const imageSpace = imageUrl === '' ? 0 : drawHeight / 2 + 16;
  const linkTransform = 'translate(' + imageSpace + 'px, 0px) rotate(' + (-angle) + 'deg)';

  return (
    <>
      { !(editPreview || linkPreview) &&
        <HighlightSvg viewBox={viewBoxValue} width={length} height={drawHeight}
          preserveAspectRatio="none"
          transform={transformValue}
          drawHeight={drawHeight} selected={selected}
          onDoubleClick={() => arrowMenuClicked ? arrowMenuClicked.edit(id) : undefined}
          onContextMenu={(ev) => onContextMenu ? onContextMenu(ev) : undefined}
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width={length} height={drawHeight}
            stroke="none" fill="#bbdefb" rx="8" ry="8"
            style={{opacity: selected ? 1 : 0, transition: 'opacity 300ms'}}
          />
        </HighlightSvg>
      }
      { !(editPreview || linkPreview) &&
        <ClickAreaSvg viewBox={viewBoxValue} width={length} height={drawHeight}
          preserveAspectRatio="none"
          transform={transformValue}
          drawHeight={drawHeight}
          onDoubleClick={() => arrowMenuClicked ? arrowMenuClicked.edit(id) : undefined}
          onContextMenu={(ev) => onContextMenu ? onContextMenu(ev) : undefined}
          pointerEvents="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width={length} height={drawHeight / 3} y={drawHeight / 3}
            stroke="none" fill="none" rx="8" ry="8"
            pointerEvents="all"
          />
        </ClickAreaSvg>
      }
      <ArrowSvg viewBox={viewBoxValue} width={(editPreview || linkPreview) ? '100%' : length} height={drawHeight}
        preserveAspectRatio={(editPreview || linkPreview) ? 'xMidYMid meet' : 'none'}
        transform={transformValue} style={{ transformOrigin: linkPreview ? 'center' : undefined }}
        drawHeight={drawHeight} preview={Boolean(editPreview || linkPreview)} selected={selected}
        onDoubleClick={() => arrowMenuClicked ? arrowMenuClicked.edit(id) : undefined}
        onContextMenu={(ev) => onContextMenu ? onContextMenu(ev) : undefined}
        pointerEvents="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <ArrowTail id={'marker-tail-' + elementId} data={data} markerSize={tailSize} />
        <ArrowHead id={'marker-head-' + elementId} data={data} markerSize={headSize} />
        <path d={d} stroke={data.color} strokeWidth={strokeWidth} fill="none"
          markerStart={'url(#marker-tail-' + elementId + ')'} markerEnd={'url(#marker-head-' + elementId + ')'}
          strokeDasharray={getArrowDasharray(data)}
          pointerEvents="stroke"
        />
        { imageUrl !== '' &&
          <image x={length / 2 - drawHeight / 2} y={drawHeight / 2 - drawHeight / 2}
            width={drawHeight} height={drawHeight}
            href={imageUrl}
            style={{transformOrigin: 'center', transform: labelReverse ? 'rotate(180deg)' : undefined}}
            pointerEvents="all"
          />
        }
        { labelKeys.map(key => (
          <ArrowLabel data={data}
            position={key}
            length={length}
            dStart={dStart}
            dEnd={dEnd}
            reverse={labelReverse}
          />
        )) }
        { (!editPreview && data.links && data.links.length >= 1) &&
          <foreignObject x={length / 2 - linkButtonSize / 2} y={drawHeight / 2 - linkButtonSize / 2}
            width={linkButtonSize} height={linkButtonSize}
            style={{transformOrigin: 'center', transform: linkTransform}}
            pointerEvents={linkPreview ? 'none' : 'all'}
          >
            <LinkIconButton size="small" onClick={() => arrowMenuClicked ? arrowMenuClicked.jump(id) : undefined}>
              <LinkOutlinedIcon style={{color: '#2979ff'}} />
            </LinkIconButton>
          </foreignObject>
        }
      </ArrowSvg>
    </>
  );
}

export default Arrow;
